/*
 * Copyright (c) 2026 Bluemate B.V.
 *
 * Page-side helpers for keeping the service worker in sync with the
 * currently-logged-in user. Used by app.tsx on every user.id transition so
 * that incoming push clicks are routed to the right user even when multiple
 * users have ever been logged in on the same browser (admin impersonation,
 * shared device).
 */

import {ResponseData} from '@dvrd/dvr-controls';

import {registerDeviceToken} from './requestSender';
import {arrayBufferToBase64Url} from './pushCrypto';
import {getDeviceLabel, isMobileDevice, isStandalone} from './pwaUtil';
import {
    setActiveUser as idbSetActiveUser,
    clearActiveUser as idbClearActiveUser,
    getLastEndpoint as idbGetLastEndpoint,
    setLastEndpoint as idbSetLastEndpoint
} from './pushIdb';
import {getOptedIn} from '../hooks/usePushSubscription';

/** Send the SW our currently-active user so notificationclick can compare
 *  it against the user_id baked into the push payload. The SW persists this
 *  in its own IDB so it survives SW restarts (the page may not be open at
 *  click time). We also write to IDB directly so a SW that has not yet
 *  installed/activated still sees a value on next click. */
export async function notifyActiveUser(userId: string | null, displayName?: string): Promise<void> {
    if (userId) {
        // Page-side write to IDB happens immediately. If the SW message is
        // dropped (SW just terminated, controller not yet attached) the IDB
        // value is still correct. display_name is stored alongside so the
        // SW push handler can prefix multi-account notifications with a
        // friendly account label.
        await idbSetActiveUser(userId, displayName);
    } else {
        await idbClearActiveUser();
    }
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    try {
        // Wait for ready instead of relying on controller — fresh tabs do
        // not have a controller until the first navigation completes, but
        // ready resolves as soon as the registration is active.
        const reg = await navigator.serviceWorker.ready;
        const target = reg.active || navigator.serviceWorker.controller;
        if (!target) return;
        target.postMessage(
            userId ? {type: 'SET_ACTIVE_USER', user_id: userId, display_name: displayName} : {type: 'CLEAR_ACTIVE_USER'}
        );
    } catch (_err) {
        /* best-effort — IDB write already happened */
    }
}

/** Re-register the browser's current push subscription under the currently-
 *  logged-in user (request.user), but only if THIS user has explicitly
 *  opted in on this device. Safe to call after every login / account
 *  switch / admin impersonation.
 *
 *  Multi-user design: we never unsubscribe the browser when switching
 *  users. Each user that has opted in keeps their own row pointing at the
 *  same endpoint. Pushes for any opted-in user arrive at this browser and
 *  the SW routes the click to the right user via payload.user_id.
 *
 *  Guard against silent opt-in: without the opt-in check, every login (and
 *  every admin impersonation step) would auto-create a backend row even
 *  for users who never enabled push on this device — UI would read OFF
 *  while pushes silently flow in the background. By requiring the per-user
 *  opted-in flag we keep UI and backend state aligned. */
export async function ensureBackendRegistered(userId: string | null, accountId: string | null): Promise<void> {
    if (!userId || !accountId) return;
    if (!getOptedIn(userId, accountId)) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    // On phones only the INSTALLED app may (re)register: a browser-context
    // keep-alive would relabel the shared endpoint from "(installed app)" to
    // a plain browser row (the downgrade every uninstall used to leave
    // behind) and would keep a zombie subscription registered. The installed
    // app refreshes the registration on its own launches; desktop browsers
    // are unaffected.
    if (isMobileDevice() && !isStandalone()) return;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) return; // No subscription yet — user has not enabled push
        const p256dh = arrayBufferToBase64Url(sub.getKey('p256dh'));
        const auth = arrayBufferToBase64Url(sub.getKey('auth'));
        if (!p256dh || !auth) return;
        const platformLabel = await getDeviceLabel();
        // If the endpoint rotated since our last register (e.g. iOS Safari
        // re-subscribed with a new endpoint), pass the previously-persisted one
        // so the backend reaps the now-dead row instead of leaving a duplicate.
        const oldEndpoint = await idbGetLastEndpoint(userId);
        const previousEndpoint = oldEndpoint && oldEndpoint !== sub.endpoint ? oldEndpoint : undefined;
        await new Promise<void>(resolve => {
            registerDeviceToken({
                data: {
                    account_id: accountId,
                    channel: 'WEB_PUSH',
                    endpoint: sub.endpoint,
                    p256dh,
                    auth,
                    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                    platform: platformLabel,
                    ...(previousEndpoint ? {previous_endpoint: previousEndpoint} : {})
                },
                callback: (_data: ResponseData) => resolve()
            });
        });
        await idbSetLastEndpoint(userId, sub.endpoint);
    } catch (_err) {
        /* best-effort */
    }
}
